/* eslint-disable no-restricted-imports */
import * as React from 'react';

import { Heading, Switch, Text } from '@/components/ui';
import { WM_FLAT_SURFACE_CLASSNAME } from '@/components/ui/surfaceStyles';

// Tokens are organised the way you reach for them: pick a family, then pick
// the tone within that family. Every shade we paint anywhere in the app is
// declared here — there are no opacity-modifier shortcuts (`/N`) used as
// proxies for tints, except the documented `hover:bg-{color}/{N}` darken
// pattern (see Rules).
const SEMANTIC_PAIRS = [
    {
        group: 'Surface',
        tokens: [
            {
                name: 'background',
                description: 'Page background.',
                surface: 'tw-bg-background',
                border: true,
                foreground: 'tw-text-foreground',
                cssVar: '--background'
            },
            {
                name: 'surface',
                description: 'Cards, panels, raised surfaces over background.',
                surface: 'tw-bg-surface',
                border: true,
                foreground: 'tw-text-foreground',
                cssVar: '--surface'
            },
            {
                name: 'foreground',
                description: 'Primary body text and icons.',
                surface: 'tw-bg-foreground',
                foreground: 'tw-text-background',
                cssVar: '--foreground'
            },
            {
                name: 'muted-foreground',
                description: 'Secondary / dimmed text.',
                surface: 'tw-bg-muted-foreground',
                foreground: 'tw-text-background',
                cssVar: '--muted-foreground'
            },
            {
                name: 'border',
                description: 'Separators and component borders.',
                surface: 'tw-bg-border',
                foreground: 'tw-text-foreground',
                cssVar: '--border'
            },
            {
                name: 'divider',
                description: 'Linework for dense grid subdivisions (Schedule grid lines, future table cell borders). Darker than `border` so it reads cleanly over both white and dark fills.',
                surface: 'tw-bg-divider',
                foreground: 'tw-text-foreground',
                cssVar: '--divider'
            },
            {
                name: 'divider-strong',
                description: 'Higher-contrast variant of `divider` for grid linework that frames filled / emphasised cells (e.g. the Schedule paints this around active quarters via 1px outset shadow). Use it to keep dense grids legible without darkening the whole grid.',
                surface: 'tw-bg-divider-strong',
                foreground: 'tw-text-foreground',
                cssVar: '--divider-strong'
            },
            {
                name: 'input',
                description: 'Border colour for form fields (Input, TextArea, Select, the disabled / unchecked Switch).',
                surface: 'tw-bg-input',
                foreground: 'tw-text-foreground',
                cssVar: '--input'
            },
            {
                name: 'accent',
                description: 'Soft tinted fill for hover and selected-but-passive states (InputOTP slot, Schedule hover, Schedule range middle, dropdown highlights).',
                surface: 'tw-bg-accent',
                border: true,
                foreground: 'tw-text-foreground',
                cssVar: '--accent'
            },
            {
                name: 'overlay-strong',
                description: 'Modal backdrop used by Dialog. Consumed only via the Backdrop primitive (`full` shape) — never used directly by feature code.',
                surface: 'tw-bg-overlay-strong',
                border: true,
                foreground: 'tw-text-background',
                cssVar: '--overlay @ alpha 0.8'
            },
            {
                name: 'overlay-soft',
                description: 'Lighter overlay used by the Backdrop primitive default shape (decorative scrim filling its parent).',
                surface: 'tw-bg-overlay-soft',
                border: true,
                foreground: 'tw-text-background',
                cssVar: '--overlay @ alpha 0.55'
            }
        ]
    },
    {
        group: 'Brand · Primary family',
        tokens: [
            {
                name: 'primary',
                description: 'Brand colour — primary actions, selected state, focus ring. Pairs with primary-foreground for text/icons.',
                surface: 'tw-bg-primary',
                foreground: 'tw-text-primary-foreground',
                cssVar: '--primary'
            },
            {
                name: 'primary-surface',
                description: 'Tinted fill paired with primary text — ContentHeader icon shell, Plans signup banner.',
                surface: 'tw-bg-primary-surface',
                border: true,
                foreground: 'tw-text-primary',
                cssVar: '--primary-surface'
            }
        ]
    },
    {
        group: 'Brand · Secondary family',
        tokens: [
            {
                name: 'secondary',
                description: 'Accent colour — sparing use for supporting actions. Pairs with secondary-foreground for text/icons.',
                surface: 'tw-bg-secondary',
                foreground: 'tw-text-secondary-foreground',
                cssVar: '--secondary'
            },
            {
                name: 'secondary-surface',
                description: 'Tinted fill paired with secondary text in outline buttons.',
                surface: 'tw-bg-secondary-surface',
                border: true,
                foreground: 'tw-text-secondary',
                cssVar: '--secondary-surface'
            }
        ]
    },
    {
        group: 'Feedback · Base',
        tokens: [
            {
                name: 'info',
                description: 'Informational callouts and neutral alerts. Pairs with info-foreground for text/icons.',
                surface: 'tw-bg-info',
                foreground: 'tw-text-info-foreground',
                cssVar: '--info'
            },
            {
                name: 'success',
                description: 'Successful outcomes and positive state. Pairs with success-foreground for text/icons.',
                surface: 'tw-bg-success',
                foreground: 'tw-text-success-foreground',
                cssVar: '--success'
            },
            {
                name: 'warning',
                description: 'Warnings and at-risk state. Pairs with warning-foreground for text/icons.',
                surface: 'tw-bg-warning',
                foreground: 'tw-text-warning-foreground',
                cssVar: '--warning'
            },
            {
                name: 'destructive',
                description: 'Errors and destructive actions. Pairs with destructive-foreground for text/icons.',
                surface: 'tw-bg-destructive',
                foreground: 'tw-text-destructive-foreground',
                cssVar: '--destructive'
            }
        ]
    },
    {
        group: 'Feedback · Surface',
        tokens: [
            {
                name: 'info-surface',
                description: 'Tinted fill for info Alerts, Panels, Toasts, infoOutline buttons.',
                surface: 'tw-bg-info-surface',
                border: true,
                foreground: 'tw-text-info',
                cssVar: '--info-surface'
            },
            {
                name: 'success-surface',
                description: 'Tinted fill for success Alerts, Panels, Toasts.',
                surface: 'tw-bg-success-surface',
                border: true,
                foreground: 'tw-text-success',
                cssVar: '--success-surface'
            },
            {
                name: 'warning-surface',
                description: 'Tinted fill for warning Alerts, Panels, Toasts.',
                surface: 'tw-bg-warning-surface',
                border: true,
                foreground: 'tw-text-warning',
                cssVar: '--warning-surface'
            },
            {
                name: 'destructive-surface',
                description: 'Tinted fill for destructive Alerts, Panels, Toasts, destructiveOutline buttons.',
                surface: 'tw-bg-destructive-surface',
                border: true,
                foreground: 'tw-text-destructive',
                cssVar: '--destructive-surface'
            }
        ]
    },
    {
        group: 'Feedback · Border',
        tokens: [
            {
                name: 'info-border',
                description: 'Soft border paired with info-surface fills.',
                surface: 'tw-bg-info-border',
                foreground: 'tw-text-info',
                cssVar: '--info-border'
            },
            {
                name: 'success-border',
                description: 'Soft border paired with success-surface fills.',
                surface: 'tw-bg-success-border',
                foreground: 'tw-text-success',
                cssVar: '--success-border'
            },
            {
                name: 'warning-border',
                description: 'Soft border paired with warning-surface fills.',
                surface: 'tw-bg-warning-border',
                foreground: 'tw-text-warning',
                cssVar: '--warning-border'
            },
            {
                name: 'destructive-border',
                description: 'Soft border paired with destructive-surface fills.',
                surface: 'tw-bg-destructive-border',
                foreground: 'tw-text-destructive',
                cssVar: '--destructive-border'
            }
        ]
    }
];

const CHART_COLORS = [
    { name: 'chart-1', cssVar: '--chart-1' },
    { name: 'chart-2', cssVar: '--chart-2' },
    { name: 'chart-3', cssVar: '--chart-3' },
    { name: 'chart-4', cssVar: '--chart-4' },
    { name: 'chart-5', cssVar: '--chart-5' },
    { name: 'chart-6', cssVar: '--chart-6' },
    { name: 'chart-7', cssVar: '--chart-7' },
    { name: 'chart-8', cssVar: '--chart-8' }
];

// Convert a computed `rgb(...)` / `rgba(...)` string into an uppercase hex.
// Returns 6-digit hex when fully opaque, 8-digit (#RRGGBBAA) otherwise.
const computedBackgroundToHex = (computedValue) => {
    const match = computedValue.match(/rgba?\((\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)(?:,\s*(\d*\.?\d+))?\)/);

    if (!match) {
        return null;
    }

    const [, r, g, b, a] = match;
    const toHexComponent = value => Math.round(Number(value)).toString(16).padStart(2, '0').toUpperCase();
    const baseHex = `#${toHexComponent(r)}${toHexComponent(g)}${toHexComponent(b)}`;
    const alphaValue = a === undefined ? 1 : Number(a);

    if (alphaValue >= 1) {
        return baseHex;
    }

    return `${baseHex}${Math.round(alphaValue * 255).toString(16).padStart(2, '0').toUpperCase()}`;
};

// Read the resolved background colour off the painted element. Re-runs when
// `themeKey` flips so dark-mode token shifts surface accurately.
const useElementBackgroundHex = (ref, themeKey) => {
    const [hex, setHex] = React.useState(null);

    React.useEffect(() => {
        if (!ref.current) {
            return;
        }

        const computed = window.getComputedStyle(ref.current).backgroundColor;

        setHex(computedBackgroundToHex(computed));
    }, [ref, themeKey]);

    return hex;
};

const SemanticSwatch = ({ token, themeKey }) => {
    const swatchRef = React.useRef(null);
    const hex = useElementBackgroundHex(swatchRef, themeKey);

    return (
        <div className={`${WM_FLAT_SURFACE_CLASSNAME} tw-overflow-hidden tw-rounded-2xl`}>
            <div
                ref={swatchRef}
                className={`${token.surface} ${token.foreground} ${token.border ? 'tw-border tw-border-border' : ''} tw-flex tw-h-24 tw-items-end tw-p-4`}
            >
                <Text variant="control">Aa</Text>
            </div>
            <div className="tw-space-y-1 tw-p-4">
                <Text as="p" variant="control">{token.name}</Text>
                <Text as="p" tone="muted" variant="caption">
                    <code>{token.cssVar}</code>
                </Text>
                {hex && (
                    <Text as="p" tone="muted" variant="caption">
                        <code>{hex}</code>
                    </Text>
                )}
                <Text as="p" tone="muted" variant="body-sm">{token.description}</Text>
            </div>
        </div>
    );
};

const ChartSwatch = ({ token }) => {
    const swatchRef = React.useRef(null);
    const hex = useElementBackgroundHex(swatchRef);

    return (
        <div className={`${WM_FLAT_SURFACE_CLASSNAME} tw-overflow-hidden tw-rounded-2xl`}>
            <div
                ref={swatchRef}
                className="tw-h-20"
                style={{ backgroundColor: `var(${token.cssVar})` }}
            />
            <div className="tw-space-y-1 tw-p-4">
                <Text as="p" variant="control">{token.name}</Text>
                <Text as="p" tone="muted" variant="caption">
                    <code>{token.cssVar}</code>
                </Text>
                {hex && (
                    <Text as="p" tone="muted" variant="caption">
                        <code>{hex}</code>
                    </Text>
                )}
            </div>
        </div>
    );
};

// Wrapper element adds/removes `dark` class without mutating <html>.
// The Tailwind config uses `darkMode: 'class'`, so any descendant styled
// with dark: variants will respond. We scope the toggle so the rest of the
// page keeps its light theme.
const DarkModeSurface = ({ dark, children }) => (
    <div
        className={`${dark ? 'dark' : ''} ${WM_FLAT_SURFACE_CLASSNAME} tw-rounded-3xl tw-bg-background tw-p-6`}
    >
        {children}
    </div>
);

export default function ColorsFoundation() {
    const [dark, setDark] = React.useState(false);

    return (
        <div className="tw-space-y-8">
            <header className="tw-space-y-2">
                <Text tone="muted" variant="overline">Foundation</Text>
                <Heading as="h2" variant="title-lg">Colors</Heading>
                <Text as="p" tone="muted" variant="body">
                    Tokens are organised by family — Surface, Brand (Primary, Secondary), and
                    Feedback (Info, Success, Warning, Destructive). Each Brand or Feedback
                    family ships a base, foreground, surface (tinted fill), and — for Feedback —
                    a soft border. There are no opacity-modifier shortcuts (`/N`); every shade
                    we paint is declared here. The chart palette is a separate, fixed eight-colour
                    sequence used exclusively by Highcharts series.
                </Text>
            </header>

            <DarkModeSurface dark={dark}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-pb-6">
                    <div>
                        <Heading as="h3" variant="title">Semantic tokens</Heading>
                        <Text tone="muted" variant="body-sm">
                            Toggle dark mode to preview how tokens shift between themes.
                        </Text>
                    </div>
                    <label className="tw-flex tw-items-center tw-gap-2">
                        <Text variant="control">{dark ? 'Dark' : 'Light'}</Text>
                        <Switch checked={dark} onCheckedChange={setDark} />
                    </label>
                </div>

                <div className="tw-space-y-6">
                    {SEMANTIC_PAIRS.map(section => (
                        <section key={section.group} className="tw-space-y-3">
                            <Text tone="muted" variant="overline">{section.group}</Text>
                            <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4">
                                {section.tokens.map(token => (
                                    <SemanticSwatch key={token.name} token={token} themeKey={dark ? 'dark' : 'light'} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </DarkModeSurface>

            <section className="tw-space-y-3">
                <div className="tw-space-y-1">
                    <Heading as="h3" variant="title">Chart palette</Heading>
                    <Text tone="muted" variant="body-sm">
                        Series 1 – 8. Defined as raw hex so Highcharts receives a stable
                        sequence across themes.
                    </Text>
                </div>
                <div className="tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-grid-cols-4 lg:tw-grid-cols-8">
                    {CHART_COLORS.map(token => (
                        <ChartSwatch key={token.name} token={token} />
                    ))}
                </div>
            </section>
        </div>
    );
}
